import { NavLink } from '@remix-run/react';
import { classNames, getIconUrl, onIconError, prettyValue, roundALR } from '~/lib/util';
import PopoverAPY from './popover-apy';

export type PositionData = {
	pair: any;
	baseToken: any;
	quoteToken: any;
	lpToken: any;
	poolState: any;
	isBase: boolean;
	amount: string;
	usdValue: number;
	share: number;
	radixRewards: boolean;
};

export function LiquidityPositionRow({ row, showALR = true }: { row: PositionData, showALR?: boolean }) {
	const share = row.share;
	const apy = row.isBase ? row.pair.baseAPY : row.pair.quoteAPY;

	return (
		<>
			<tr key={row.lpToken.address} className="dark:hover:bg-primary-800 hover:bg-neutral-100">
				<td className="py-4 pl-0 pr-3 font-bold max-sm:pb-2 lg:pl-4">
					<div className="flex shrink items-center">
						<div className="h-8 w-8">
							<img className="bg-background-900 h-8 w-8 rounded-full" src={getIconUrl(row.lpToken.iconUrl, 32)} alt="" />
						</div>
						<span className="ml-3">
							{row.lpToken.name.replace("Defiplaza ", "").replace("DefiPlaza ", "")}
							{showALR && row.radixRewards ? <span className="text-xs rounded bg-black/10 text-black/50 dark:bg-white/10 dark:text-white/50 ml-3 p-1" title="This pool is incentivized by the Radix Rewards program.">RR</span> : null}

						</span>
					</div>
				</td>
				<td className="hidden px-3 py-4 text-sm font-medium lg:table-cell">
					<p className={classNames(row.isBase ? "text-primary-700 dark:text-white" : "dark:text-primary-200 text-black", "flex space-x-2")}>
						<img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(row.baseToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
						<span>{prettyValue(share * (row.isBase ? row.poolState.vaults[0].amount : row.poolState.vaults[1].amount))}</span>
					</p>
					<p className={classNames(row.isBase ? "dark:text-primary-200 text-black" : "text-primary-700 dark:text-white", "flex space-x-2")}>
						<img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(row.quoteToken.iconUrl)} alt="" />
						<span>{prettyValue(share * (row.isBase ? row.poolState.vaults[1].amount : row.poolState.vaults[0].amount))}</span>
					</p>
				</td>
				<td className="hidden px-3 py-4 font-medium lg:table-cell">{showALR ? '$' + prettyValue(row.usdValue) : ''}</td>
				<td className={classNames("hidden px-3 py-4 font-medium lg:table-cell")}>{Math.round(share * 10000) / 100}%</td>
				{showALR ? <td className={classNames("hidden px-3 py-4 font-medium lg:table-cell")}>{roundALR(apy)}%</td> : null}
				<td className="py-4 pl-3 pr-0 text-right text-sm font-medium max-sm:pb-2 sm:pr-0 lg:pr-4">
					{showALR ? <NavLink
						to={`/liquidity/add/${row.pair.address}/?direction=${row.isBase ? "base" : "quote"}`}
						className={({ isActive }) => (isActive ? "btn-white-table" : "btn-primary-table")}
					>
						{({ isActive, isPending }) => (
							<>
								{isActive ? (
									<>
										<span>Adding</span>
										<svg className="ml-1 h-4 w-4" viewBox="2 2 16 16" xmlns="http://www.w3.org/2000/svg">
											<path
												d="M10.986 5.456a.608.608 0 0 0-.827 0 .564.564 0 0 0 0 .809l3.233 3.16H5.29a.579.579 0 0 0-.584.572.58.58 0 0 0 .584.572h8.102l-3.233 3.16a.564.564 0 0 0 0 .81c.11.107.257.167.414.167.155 0 .302-.06.413-.168l4.072-3.982a.778.778 0 0 0 0-1.119l-4.072-3.98z"
												fill="currentColor"
												fillRule="nonzero"
											></path>
										</svg>
									</>
								) : (
									"Add"
								)}
							</>
						)}
					</NavLink> : null}

					<NavLink
						to={`/liquidity/remove/${row.pair.address}/${row.isBase ? "base" : "quote"}`}
						className={({ isActive }) => (isActive ? "btn-white-table ml-1" : "btn-primary-table ml-1")}
					>
						{({ isActive, isPending }) => <>{isActive ? "Remove" : "Remove"}</>}
					</NavLink>
				</td>
			</tr>
			<tr className="border-b-primary-200 border-b lg:hidden">
				<td colSpan={2}>
					<div className="mx-4 my-2 mt-4 flex items-center justify-between">
						<p>
							<p className={classNames(row.isBase ? "text-primary-700 dark:text-white" : "dark:text-primary-200 text-black", "flex items-center space-x-2")}>
								<img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(row.baseToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
								<span>{prettyValue(share * (row.isBase ? row.poolState.vaults[0].amount : row.poolState.vaults[1].amount))}</span>
							</p>
							<p className={classNames(row.isBase ? "dark:text-primary-200 text-black" : "text-primary-700 dark:text-white", "flex items-center space-x-2")}>
								<img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(row.quoteToken.iconUrl)} alt="" />
								<span>{prettyValue(share * (row.isBase ? row.poolState.vaults[1].amount : row.poolState.vaults[0].amount))}</span>
							</p>
						</p>
						<p>${prettyValue(row.usdValue)}</p>
						{showALR ? <p>{Math.round(apy * 10000) / 100}% ALR</p> : null}
					</div>
				</td>
			</tr>
		</>
	);
}
